/**
 * SequencerSlot Component
 * Droppable and sortable slot for playlists in the sequencer
 */

import { GripVertical, X, ListMusic, Clock } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { SequencerSlot as SequencerSlotType } from '../../stores/sequencer';

interface SequencerSlotProps {
  slot: SequencerSlotType;
  position: number;
  onRemove: () => void;
  onModeChange: (mode: 'OPTIMIZE' | 'FORCE_NEXT') => void;
  onRepeatCountChange: (count: number) => void;
}

export function SequencerSlot({
  slot,
  position,
  onRemove,
  onModeChange,
  onRepeatCountChange,
}: SequencerSlotProps) {
  // Sortable: for reordering slots
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: slot.id,
    data: { type: 'sequencer-slot', slotId: slot.id },
  });

  // Droppable: for receiving playlist drops
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `sequencer-slot-drop-${slot.id}`,
    data: { type: 'sequencer-slot', slotId: slot.id },
  });

  // Combine refs
  const setNodeRef = (node: HTMLElement | null) => {
    setSortableRef(node);
    setDroppableRef(node);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Stopsets render differently
  const isStopset = slot.type === 'stopset';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        p-4 rounded-lg border-2 transition-all
        ${
          isStopset
            ? // Stopset styling (amber)
              'border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/15'
            : slot.playlist
              ? // Crate with playlist (emerald)
                'border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/15'
              : // Empty crate slot (dashed)
                'border-zinc-700 border-dashed bg-zinc-800/30 hover:bg-zinc-800/50'
        }
        ${isOver && !isStopset ? 'border-emerald-500 bg-emerald-500/20 ring-2 ring-emerald-500/50' : ''}
        ${isDragging ? 'cursor-grabbing z-50' : ''}
      `}
    >
      <div className="flex items-center gap-3">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 p-1 cursor-grab active:cursor-grabbing text-zinc-500 hover:text-zinc-300 transition-colors"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Position Badge */}
        <div
          className={`
            flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
            ${
              isStopset
                ? 'bg-amber-600/30 text-amber-300'
                : 'bg-zinc-700 text-zinc-300'
            }
          `}
        >
          {position}
        </div>

        {/* Icon */}
        <div className="flex-shrink-0">
          {isStopset ? (
            <Clock className="w-5 h-5 text-amber-400" />
          ) : (
            <ListMusic className="w-5 h-5 text-zinc-400" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {isStopset ? (
            // Stopset content
            <div>
              <div className="font-medium text-amber-200">Stopset Break</div>
              <div className="text-xs text-amber-400/70">
                Scheduled break insertion
              </div>
            </div>
          ) : slot.playlist ? (
            // Crate with playlist
            <div>
              <div className="font-medium text-zinc-100 truncate">
                {slot.playlist.name}
              </div>
              <div className="text-xs text-zinc-500">
                {slot.playlist.tracksCount} track{slot.playlist.tracksCount !== 1 ? 's' : ''}
              </div>
            </div>
          ) : (
            // Empty crate slot
            <div className="text-zinc-500 text-sm">
              Drop playlist here...
            </div>
          )}
        </div>

        {/* Mode Selector (only for crates with playlists) */}
        {!isStopset && slot.playlist && (
          <select
            value={slot.mode}
            onChange={(e) =>
              onModeChange(e.target.value as 'OPTIMIZE' | 'FORCE_NEXT')
            }
            className="px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 hover:border-zinc-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-sm text-zinc-300 transition-colors cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <option value="OPTIMIZE">Optimize</option>
            <option value="FORCE_NEXT">Force Next</option>
          </select>
        )}

        {/* Repeat Count Controls (only for crates with playlists) */}
        {!isStopset && slot.playlist && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700">
            <span className="text-xs text-zinc-400">Repeat:</span>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onRepeatCountChange(slot.repeatCount - 1);
              }}
              disabled={slot.repeatCount <= 1}
              className="w-6 h-6 flex items-center justify-center rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed text-zinc-300 text-sm font-bold transition-colors"
              title="Decrease repeat count"
            >
              −
            </button>

            <span className="w-8 text-center text-sm font-semibold text-emerald-400">
              ×{slot.repeatCount}
            </span>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onRepeatCountChange(slot.repeatCount + 1);
              }}
              disabled={slot.repeatCount >= 10}
              className="w-6 h-6 flex items-center justify-center rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed text-zinc-300 text-sm font-bold transition-colors"
              title="Increase repeat count"
            >
              +
            </button>
          </div>
        )}

        {/* Remove Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={`
            flex-shrink-0 p-1.5 rounded-lg transition-colors
            ${
              isStopset
                ? 'bg-amber-800/30 hover:bg-amber-700/40 text-amber-400 hover:text-amber-300'
                : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-100'
            }
          `}
          title="Remove slot"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
